import { Fragment, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Quote } from "lucide-react";

import { ExtractedInstance, ExtractionPayload } from "../../DocumentAnalysisAPI";
import { isKnownClass } from "./ontologyValidation";

import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

/**
 * The objects the extraction found, as cards grouped by term.
 *
 * Three things are held apart on purpose, because conflating them is what would make the result
 * unreadable:
 *
 * - **What was found** stands at the top of the attribute table. Attributes the document did not
 *   mention are null, and null is the common case — a lease document fills two of the twenty-nine
 *   fields a `LegalPerson` has. Folding them away is not decoration; it is what lets the eye land
 *   on the four values that are there.
 * - **What did not fit** is its own marked section. It is not a defect in the extraction — the
 *   document said something the model has no room for, which is a question for the ontology and
 *   the most useful thing on the screen.
 * - **Where it came from.** Every instance carries a quotation. An extracted value without a
 *   source is a claim; with one, it can be checked in a second.
 */

const formatValue = (value: any): string => {
  if (value === null || value === undefined) return "—";
  if (typeof value === "object") return JSON.stringify(value);

  return String(value);
};

/** Long enough to recognise a value, short enough that a line stays a line. */
const SUMMARY_VALUE_CHARS = 60;

const shorten = (value: string): string =>
  value.length > SUMMARY_VALUE_CHARS ? `${value.slice(0, SUMMARY_VALUE_CHARS)}…` : value;

const filledEntries = (attributes: Record<string, any>): Array<[string, any]> =>
  Object.keys(attributes ?? {})
    .filter((key) => attributes[key] !== null && attributes[key] !== undefined && attributes[key] !== "")
    .map((key) => [key, attributes[key]] as [string, any]);

/**
 * What was found, in the step row itself.
 *
 * The cards below say everything — every declared attribute, the empty ones, the quotation, what
 * did not fit. That is the right depth for reading one instance closely and the wrong one for the
 * first look, which is always the same question: *did it find the things, and do the values look
 * right?* Answering that should not cost a click.
 *
 * So this shows exactly the attributes that have a value, and nothing else. Empty ones are the
 * majority and say only that the document was silent; they belong in the table, not here.
 */
export function InstanceSummary({ payload }: { payload: ExtractionPayload }) {
  const { t } = useTranslation();

  const instances = payload?.instances ?? [];
  if (payload?.skipped || instances.length === 0) return null;

  return (
    <ul className="mt-2 flex flex-col gap-2">
      {instances.map((instance, index) => {
        const entries = filledEntries(instance.attributes);
        const unmappedCount = Object.keys(instance.unmappedAttributes ?? {}).length;

        return (
          <li key={`${instance.term}-${index}`} className="text-xs">
            <div className="flex flex-wrap items-baseline gap-x-2">
              <span className="font-medium">{instance.term}</span>
              <span className="font-mono text-muted-foreground">
                {instance.className ?? t("documentAnalysisPanel.test.withoutClass")}
              </span>
            </div>

            {entries.length === 0 ? (
              <p className="pl-4 text-muted-foreground">{t("documentAnalysisPanel.test.nothingFound")}</p>
            ) : (
              /* Two aligned columns rather than a run-on line: the attribute names line up, so the
                 eye finds a value by its label instead of reading the whole row to the end. */
              <dl className="grid grid-cols-[minmax(0,9rem)_minmax(0,1fr)] gap-x-3 pl-4">
                {entries.map(([key, value]) => (
                  <Fragment key={key}>
                    <dt className="truncate font-mono text-muted-foreground" title={key}>
                      {key}
                    </dt>
                    <dd className="min-w-0 break-words" title={formatValue(value)}>
                      {shorten(formatValue(value))}
                    </dd>
                  </Fragment>
                ))}
              </dl>
            )}

            {/* Named here too, because it is the finding a reader would otherwise have to go
                looking for: the document said more than the class can hold. */}
            {unmappedCount > 0 ? (
              <p className="pl-4 text-amber-700 dark:text-amber-300">
                +{t("documentAnalysisPanel.test.unmapped", { count: unmappedCount })}
              </p>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}

function ClassMark({ className }: { className: string | null }) {
  const { t } = useTranslation();

  if (!className) {
    return (
      <span className="rounded border border-dashed border-muted-foreground/40 px-1.5 py-0.5 text-xs text-muted-foreground">
        {t("documentAnalysisPanel.test.withoutClass")}
      </span>
    );
  }

  // The same judgement the catalogue tab makes: a class the shipped model does not register.
  if (!isKnownClass(className)) {
    return (
      <span className="inline-flex items-center gap-1 rounded border border-destructive px-1.5 py-0.5 text-xs text-destructive">
        <AlertTriangle className="h-3 w-3" aria-hidden />
        {className}
      </span>
    );
  }

  return <span className="rounded border px-1.5 py-0.5 font-mono text-xs">{className}</span>;
}

function AttributeTable({ attributes }: { attributes: Record<string, any> }) {
  const { t } = useTranslation();
  const [showEmpty, setShowEmpty] = useState(false);

  const keys = Object.keys(attributes ?? {});
  const filled = filledEntries(attributes).map(([key]) => key);
  const empty = keys.filter((key) => filled.indexOf(key) < 0);

  const shown = showEmpty ? [...filled, ...empty] : filled;

  return (
    <div className="flex flex-col gap-1">
      {shown.length === 0 ? (
        <span className="text-xs text-muted-foreground">{t("documentAnalysisPanel.test.nothingFound")}</span>
      ) : (
        <table className="w-full text-xs">
          <tbody>
            {shown.map((key) => (
              <tr key={key} className="border-b last:border-0">
                <td className="w-[45%] py-1 pr-2 align-top font-mono text-muted-foreground">{key}</td>
                <td
                  className={`py-1 align-top ${
                    attributes[key] === null || attributes[key] === undefined ? "text-muted-foreground" : ""
                  }`}
                >
                  {formatValue(attributes[key])}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {empty.length > 0 ? (
        <Button variant="link" size="sm" className="h-auto self-start p-0" onClick={() => setShowEmpty(!showEmpty)}>
          {showEmpty
            ? t("documentAnalysisPanel.test.hideEmpty")
            : t("documentAnalysisPanel.test.showEmpty", { count: empty.length })}
        </Button>
      ) : null}
    </div>
  );
}

function InstanceCard({ instance }: { instance: ExtractedInstance }) {
  const { t } = useTranslation();

  const unmapped = instance.unmappedAttributes ?? {};
  const unmappedKeys = Object.keys(unmapped);

  return (
    <div className="rounded-md border bg-background p-3">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <span className="text-sm font-semibold">{instance.term}</span>
        <span className="text-muted-foreground">→</span>
        <ClassMark className={instance.className} />
        <span className="text-xs text-muted-foreground">
          {Math.round((instance.confidence ?? 0) * 100)} %
        </span>

        {instance.sourceRef?.quote ? (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="ml-auto inline-flex items-center gap-1 text-xs text-muted-foreground">
                  <Quote className="h-3 w-3" aria-hidden />
                  {instance.sourceRef.location || t("documentAnalysisPanel.test.source")}
                </span>
              </TooltipTrigger>
              <TooltipContent className="max-w-sm">„{instance.sourceRef.quote}"</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : null}
      </div>

      <AttributeTable attributes={instance.attributes} />

      {unmappedKeys.length > 0 ? (
        <div className="mt-2 rounded border border-amber-400/60 bg-amber-50 p-2 dark:border-amber-700 dark:bg-amber-950/40">
          <div className="mb-1 text-xs font-medium text-amber-900 dark:text-amber-200">
            {t("documentAnalysisPanel.test.unmapped", { count: unmappedKeys.length })}
          </div>
          <table className="w-full text-xs">
            <tbody>
              {unmappedKeys.map((key) => (
                <tr key={key}>
                  <td className="w-[45%] py-0.5 pr-2 align-top font-mono text-amber-900/70 dark:text-amber-200/70">
                    {key}
                  </td>
                  <td className="py-0.5 align-top">{formatValue(unmapped[key])}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}

export default function InstanceCards({ payload }: { payload: ExtractionPayload }) {
  const { t } = useTranslation();

  if (payload.skipped) {
    return (
      <p className="text-sm text-muted-foreground">
        {t("documentAnalysisPanel.test.skipped", { reason: payload.reason ?? "" })}
      </p>
    );
  }

  const instances = payload.instances ?? [];
  const report = payload.report;

  // Grouped by term, in the order the terms first appear: the catalogue lists primary entities
  // before reference ones, and the extraction follows it — so the lease comes before its parties.
  const terms: string[] = [];
  instances.forEach((instance) => {
    if (terms.indexOf(instance.term) < 0) terms.push(instance.term);
  });

  return (
    <div className="flex flex-col gap-3">
      {report ? (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
          <span>{t("documentAnalysisPanel.test.instanceCount", { count: report.instanceCount })}</span>

          {Object.keys(report.byTerm ?? {}).map((term) => (
            <span key={term}>
              · {term} {report.byTerm[term]}×
            </span>
          ))}

          {report.movedToUnmapped > 0 ? (
            <span className="text-amber-700 dark:text-amber-300">
              · {t("documentAnalysisPanel.test.movedToUnmapped", { count: report.movedToUnmapped })}
            </span>
          ) : null}

          {report.typeViolations > 0 ? (
            <span className="text-amber-700 dark:text-amber-300">
              · {t("documentAnalysisPanel.test.typeViolations", { count: report.typeViolations })}
            </span>
          ) : null}

          {report.droppedInstances > 0 ? (
            <span className="text-destructive">
              · {t("documentAnalysisPanel.test.droppedInstances", { count: report.droppedInstances })}
            </span>
          ) : null}
        </div>
      ) : null}

      {instances.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t("documentAnalysisPanel.test.noInstances")}</p>
      ) : (
        terms.map((term) => (
          <div key={term} className="flex flex-col gap-2">
            {instances
              .filter((instance) => instance.term === term)
              .map((instance, index) => (
                <InstanceCard key={`${term}-${index}`} instance={instance} />
              ))}
          </div>
        ))
      )}
    </div>
  );
}
